// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
// Libs
import { REGEXP_ONLY_DIGITS } from 'input-otp';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import useSessionStore from '@/app/store/session';
import actionsSession from '@/app/modules/session/api/actionsSession';
import actionsLogin from '../../api/actionsLogin';
// Common
import ICONS from '../../../../../common-utils/enums/icons';
import RouteUrls from '@/app/root/enums/RouteUrls';
import LOGIN_SCREENS from '@/app/modules/login/enums/LoginScreens';
// Components
import DeactivateMFAFromLogin from './DeactivateMFAFromLogin/DeactivateMFAFromLogin';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Spinner from '@/common-utils/components/renders/spinner/Spinner';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/common-utils/components/ui/inputOtp';
import { Button } from '../../../../../common-utils/components/ui/button';
// Css
import './LoginTOTP.scss';
// Lang
import { nsLogin } from '@/app/i18n/namespaces';
import { usePostHog } from '@posthog/react';

/**
 * @param {function} setShowMfaLogin
 * @param {function} showNoMfaAccess
 * @param {function} setSubmitError
 * @param {string} userEmail
 * @param {Function} exitScreen
 * @returns {JSX.Element} ForgotPassword component
 */
function LoginTOTP({ setShowMfaLogin, showNoMfaAccess, setSubmitError, userEmail, exitScreen }) {
    // State
    const [appCode, setAppCode] = useState('');

    // Store
    const { token } = useSessionStore();

    // PostHog
    const posthog = usePostHog();

    // API Call
    const { refetch } = actionsSession.getSession(false);

    const onSuccess = () => {
        refetch();
        const email = userEmail;
        posthog?.identify(email, { email });
        posthog?.capture('ConnexionUtilisateurAvecMfa', { email });
    };
    const onError = (error) => {
        const errorCode = error?.response?.data?.errorCode ?? error?.code ?? 'UNKNOWN_ERROR';
        setSubmitError(errorCode);
        setShowMfaLogin(false);
        exitScreen(LOGIN_SCREENS.MFA_LOGIN);
    };
    const submitLoginFromTotp = actionsLogin.loginFromTotp(onSuccess, onError);

    // Functions
    const handleSubmit = () => {
        if (appCode !== '') {
            submitLoginFromTotp.mutate({ totp: appCode, token });
        }
    };

    // Redirection if user is connected
    if (submitLoginFromTotp.isSuccess) {
        return <Navigate to={RouteUrls.HOME.url} />;
    }

    return (
        <div>
            {
                showNoMfaAccess ?
                    <DeactivateMFAFromLogin userEmail={userEmail} exitScreen={() => exitScreen(LOGIN_SCREENS.NO_MFA_ACCESS)} />
                    :
                    <div>
                        <div className='totp-login-title'>
                            <Entity namespace={nsLogin} entity='totp.title' />
                        </div>
                        <div className={cn('totop-input')}>
                            <Entity namespace={nsLogin} entity='totp.input_label' className={cn('font-normal')} />
                            <InputOTP
                                containerClassName={cn('justify-center mt-6 mb-6')}
                                maxLength={6}
                                pattern={REGEXP_ONLY_DIGITS}
                                value={appCode}
                                onChange={(code) => setAppCode(code)}
                            >
                                <InputOTPGroup>
                                    {[0, 1, 2].map((index) => <InputOTPSlot key={index} index={index} />)}
                                </InputOTPGroup>
                                <InputOTPSeparator />
                                <InputOTPGroup>
                                    {[3, 4, 5].map((index) => <InputOTPSlot key={index} index={index} />)}
                                </InputOTPGroup>
                            </InputOTP>
                        </div>
                        <div className='totop-login-submission'>
                            <Button
                                variant='primary'
                                namespace={nsLogin}
                                entity='totp.submit'
                                icon={ICONS.USER}
                                size='lg'
                                onClick={handleSubmit}
                                disabled={submitLoginFromTotp.isLoading}
                            />
                            {submitLoginFromTotp.isLoading && <Spinner className={'mt-6'} />}
                        </div>
                    </div>
            }
        </div>
    );
}

LoginTOTP.propTypes = {
    setShowMfaLogin: PropTypes.func,
    showNoMfaAccess: PropTypes.bool,
    setSubmitError: PropTypes.func,
    userEmail: PropTypes.string,
    exitScreen: PropTypes.func
};

export default LoginTOTP;